/**
 * Created by Amit Panghal
 */
import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {ISymbolStateConfig, ISymbolStates} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {ILayersConfig} from "@nolimitcity/slot-game/bin/core/stage/ILayersConfig";
import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Back, Ease} from "gsap";
import {TractorBeamLayerConfig} from "../config/TractorBeamLayerConfig";
import {TractorBeamGameAssets} from "./TractorBeamGameAssets";

export interface TractorBeamSymbolStates extends ISymbolStates {
    noWin:ISymbolStateConfig;
    attention:ISymbolStateConfig;
}

export interface ITractorBeamGameConfig extends IGameConfig {
    GAME_SYMBOLS:string[];
    LOW_WIN_GAME_SYMBOLS:string[];
    MID_WIN_GAME_SYMBOLS:string[];
}

export class TractorBeamGameConfig implements ITractorBeamGameConfig {

    public static instance:TractorBeamGameConfig = new TractorBeamGameConfig();

    BROWSER_FPS:number = 30;

    GAME_WIDTH:number = 1280;
    GAME_HEIGHT:number = 720;

    DEFAULT_LAYER_SCALE_OPTION:IDeviceDependentConfig<LayerScaleOption> = TractorBeamLayerConfig.defaultLayerScaleOptions;

    LAYERS:ILayersConfig = TractorBeamLayerConfig.getConfig();

    BET_WIN_MODE:BetWinMode = BetWinMode.BET_LINE;
    SPIN_MODE:SpinMode = SpinMode.SPIN;

    REEL_AREA_POS_X:number = 232;
    REEL_AREA_POS_Y:number = 85;
    REEL_AREA_POS_Y_PORTRAIT = 185;

    REEL_AREA_WIDTH:number = 840;
    REEL_AREA_HEIGHT:number = 615;

    REELS_NUM:number = 5;
    REEL_DISPLAY_POSITIONS:[number, number][] = [ // TODO: Strange behavior...
        [167 * 0.5, 30],
        [167 * 0.5 + 167, 30],
        [167 * 0.5 + 167 * 2, 30],
        [167 * 0.5 + 167 * 3, 30],
        [167 * 0.5 + 167 * 4, 30]];
    SYMBOLS_NUM_IN_REEL:[number, number, number, number, number] = [1, 0, 3, 0, 1];

    SYMBOL_STATES:TractorBeamSymbolStates = {
        normal : {
            prio : 0,
            keyword : "normal",
            texturesCreator : (symName:string, stateKey:string) => {
                return TractorBeamGameAssets.getSymbolStateTextures(symName, "spin");
            }
        },
        spin : {
            prio : 1,
            keyword : "spin",
            texturesCreator : (symName:string, stateKey:string) => {
                return TractorBeamGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        noWin : {
            prio : 0,
            keyword : "noWin",
            texturesCreator : (symName:string, stateKey:string) => {
                return TractorBeamGameAssets.getSymbolStateTextures(symName);
            }
        },
        attention : {
            prio : 4,
            keyword : "attention",
            texturesCreator : (symName:string, stateKey:string) => {
                return TractorBeamGameAssets.getSymbolStateTextures(symName);
            }
        },
        hidden : {
            prio : 2,
            keyword : "hidden",
            texturesCreator : (symName:string, stateKey:string) => {
                return TractorBeamGameAssets.getSymbolStateTextures(symName);
            }
        },
        win : {
            prio : 3,
            keyword : "win",
            texturesCreator : (symName:string, stateKey:string) => {
                return TractorBeamGameAssets.getSymbolStateTextures(symName);
            }
        }
    };
    SYMBOL_WIDTH:number = 167;
    SYMBOL_HEIGHT:number = 167;
    SYMBOL_BOTTOM_PADDING:number = 0;

    REEL_SPIN_TIME:number = 0.9;
    REEL_FAST_SPIN_SPIN_TIME:number = 0.2;
    MIN_STOPPING_TIME:number = 2;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION:number = 0.2;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY:number = 0.3;
    REEL_SPIN_START_EASE:Ease = Back.easeIn.config(4);
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION:number = 0;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY:number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG:[number, number] = [1.5, 0.95];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG:[number, number] = [1.2, 0.75];

    REEL_SPIN_START_SPEED:number = 0.41;
    REEL_SPIN_SPIN_SPEED:number = 0.04;
    REEL_NEAR_WIN_SPIN_SPEED:number = 0.03;
    REEL_SPIN_STOP_SPEED:number = 0.62;
    REEL_SPIN_QUICK_STOP_SPEED:number = 0.5;

    REEL_STOP_DELAY:number = 0.3;
    REEL_QUICK_STOP_DELAY:number = 0.1;
    REEL_NEAR_WIN_STOP_DELAY:number[] = [1, 1, 2, 2, 2];
    GAME_SYMBOLS:["L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "S", "W"];
    LOW_WIN_GAME_SYMBOLS:["L1", "L2", "L3", "L4"];
    MID_WIN_GAME_SYMBOLS:["M1", "M2", "M3", "M4"];

    PRE_DEF_REEL_SETS:any = {
        "BASE_REELSET" : [
            ["L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4",
                "L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4"],
            ["L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4",
                "L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4"],
            ["L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4",
                "L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4"],
            ["L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4",
                "L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4"],
            ["L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4",
                "L1", "L2", "L3", "L4", "M1", "S", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4"]
        ],
        "FREESPIN_REELSET" : [
            ["L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4"],
            ["L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4"],
            ["L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4"],
            ["L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4"],
            ["L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "M1", "M2", "M3", "M4", "W", "L1", "L2", "L3", "L4", "S", "M1", "M2", "M3", "M4"],
        ]
    };

    // Generated reel sets
    REEL_SETS:any = this.ReelSetCreation(["RANDOM_STACK_M1", "RANDOM_STACK_M2", "RANDOM_STACK_M3", "RANDOM_STACK_M4"], ["M1", "M2", "M3", "M4"]);


    WIN_RATIOS:IWinRatios = {
        normalWin : [
            {
                ratio : 0,
                text : "Tiny Win",
                duration : 0,
                type : WinRatioType.TINY
            },
            {
                ratio : 1.5,
                text : "Small Win",
                duration : 0,
                type : WinRatioType.SMALL
            },
            {
                ratio : 3,
                text : "Medium Win",
                duration : 1.46,
                type : WinRatioType.MEDIUM
            },
            {
                ratio : 5,
                text : "Large Win",
                duration : 1.86,
                type : WinRatioType.LARGE
            }
        ],
        bigWin : [
            {
                ratio : 15,
                text : "Big Win",
                duration : 6.952
            },
            {
                ratio : 25,
                text : "Mega Win",
                duration : 6.543
            },
            {
                ratio : 50,
                text : "Super Mega Win",
                duration : 6.503
            }
        ]
    };

    BIG_WIN_FADE_IN_TIMING_MS:number = 5000;
    BIG_WIN_FADE_OUT_TIMING_MS:number = 200;
    PERFORMANCE_LOGGER: boolean = true;
    // GAME_TWEAKER_CONFIG:IGameTweakerConfig = {
    //     layer : this.LAYERS.TWEAK_MODULE.name,
    //     baseWindowWidth : 400,
    //     moduleHeight : 32,
    //     copyAllButtonText : "Copy All",
    //     buttonSkinIdle : "button",
    //     buttonSkinOver : "button",
    //     buttonSkinDown : "button",
    //     buttonSkinDisabled : "button-disabled"
    // };

    WIN_BELOW_STAKE: boolean = false;  // #226

    private constructor() {
        if(TractorBeamGameConfig.instance) {
            throw new Error("TractorBeamGameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }

    private ReelSetCreation(setType:string[], exceptionSymList:any [] = []):any {
        // https://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
        function shuffleArray(array:string[], exceptionSym?:string):string[] {
            for(let i = array.length - 1; i > 0; i--) {
                if(exceptionSym && array[i] === exceptionSym) {
                    // Don't shuffle
                } else {
                    const j = Math.floor(Math.random() * (i + 1));
                    [array[i], array[j]] = [array[j], array[i]];
                }
            }
            return array;
        }

        let temp:any = {};
        for(let r:number = 0; r < setType.length; r++) {
            temp[setType[r]] = [];
            const exSym:string = exceptionSymList[r];
            for(var i:number = 0; i < this.REELS_NUM; i++)
                if(exceptionSymList.length > 0) {
                    temp[setType[r]].push(shuffleArray([exSym, exSym, exSym, "M2", "L2", "M4", "W", exSym, exSym, exSym, "L3", "L4", exSym, exSym, exSym, "L1", "L4", exSym, exSym, exSym,], exceptionSymList[r]));
                } else {
                    temp[setType[r]].push(shuffleArray(["M1", "M1", "M1", "M2", "L2", "M4", "S", "W", "M1", "M1", "M1", "L3", "L4", "S", "M1", "M1", "M1"]));
                }
        }

        temp["BASE_REELSET"] = this.PRE_DEF_REEL_SETS["BASE_REELSET"];
        temp["FREESPIN_REELSET"] = this.PRE_DEF_REEL_SETS["FREESPIN_REELSET"];

        return temp;
    }
}
